import { SortDir } from '__generated__';
import { SortingState } from '@tanstack/react-table';
import { History } from 'history';
import queryString from 'query-string';
import { useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router';

import { SortDirection, SortDirType } from 'shared/components/data/Sort';
import { SortOptionType } from 'shared/components/internal/SortMenu';
import { deserializeSort, serializeSort } from 'shared/utils/query';

interface SortConfig<T = any> {
  defaultSortDir: SortDirType;
  defaultSort: string;
  sortOptions: SortOptionType<T>;
}

interface DeserializedFilterQueryParams {
  q: string;
  offset: number;
}

interface DeserializedSortQueryParams<T = any> {
  sorts: SortDirection<T>[];
  sort: T;
  sortDir: SortDir;
}

/**
 * Deserialize a query string into a object.
 */
const deserializeFilterQueryParams = (
  search: History<any>['location']['search']
): DeserializedFilterQueryParams => {
  const { q, offset } = queryString.parse(search);

  const queryProp = q && Array.isArray(q) ? q[0] : q;
  const offsetProp = offset && Array.isArray(offset) ? offset[0] : offset;
  return {
    q: queryProp || '',
    offset: Number(offsetProp) || 0,
  };
};

const deserializeSortQueryParams = (
  history: History<any>,
  sortConfig: SortConfig
): DeserializedSortQueryParams => {
  const { sorts: sortsParam } = queryString.parse(history.location.search);
  const defaultSort = [
    { prop: sortConfig.defaultSort, direction: sortConfig.defaultSortDir },
  ];

  let sorts = deserializeSort(sortsParam as string, defaultSort);

  const [sortVal] = sorts;

  let sortProp = sortConfig.sortOptions[sortVal.prop]?.name;
  let sortDirProp =
    sortVal.direction === SortDir.Asc || sortVal.direction === undefined
      ? SortDir.Asc
      : SortDir.Desc;

  // The query string has wrong sort params, so fix them to the default.
  if (sortProp === undefined) {
    sorts = defaultSort;
    sortProp = sortConfig.sortOptions[sortConfig.defaultSort]?.name;
    sortDirProp =
      sortConfig.defaultSortDir === SortDir.Asc ? SortDir.Asc : SortDir.Desc;

    onSortChange(history, sorts);
  }

  return {
    sorts,
    sort: sortProp,
    sortDir: sortDirProp,
  };
};

/**
 * Construct the default parameters for a given state and update history object.
 */
const setDefaults = (history: History<any>, sortConfig?: SortConfig) => {
  const qs = queryString.parse(history.location.search);
  const { offset } = deserializeFilterQueryParams(history.location.search);
  const sortParams = sortConfig
    ? deserializeSortQueryParams(history, sortConfig)
    : undefined;

  history.replace({
    ...history.location,
    search: queryString.stringify({
      ...(sortParams ? { sorts: serializeSort(sortParams.sorts) } : {}),
      offset,
      ...qs,
      q: qs.q,
    }),
  });
};

/**
 * Given a offset change, update our history object.
 */
const onOffsetChange = (history: History<any>, offset: number) => {
  const qs = queryString.parse(history.location.search);

  history.push({
    ...history.location,
    search: queryString.stringify({
      ...qs,
      offset,
    }),
  });
};

/**
 * Given a query change, update our history object.
 */
const onQueryChange = (history: History<any>, q: string) => {
  const qs = queryString.parse(history.location.search);

  history.push({
    ...history.location,
    search: queryString.stringify({
      ...qs,
      q,
      offset: 0,
    }),
  });
};

/**
 * Given a sort change, update our history object.
 */
const onSortChange = (history: History<any>, sort: SortDirection<any>[]) => {
  const qs = queryString.parse(history.location.search);

  history.push({
    ...history.location,
    search: queryString.stringify({
      ...qs,
      offset: 0,
      sorts: serializeSort(sort),
    }),
  });
};

const mapSortingState = (sorts: SortingState) => {
  return sorts.map((s) => ({
    prop: s.id,
    direction: s.desc ? SortDir.Desc : SortDir.Asc,
  }));
};

/**
 * Hook to facilitate dealing with sorting/filtering/paging.
 */
export const useFilter = (sortConfig?: SortConfig) => {
  const history = useHistory();
  // location should not be accessed via history because history is mutable and
  // may break assumptions about hook lifecycles, i.e, it won't trigger an
  // update https://reactrouter.com/web/api/location
  const location = useLocation();

  useEffect(() => {
    const qs = queryString.parse(location.search);

    if (qs.offset === undefined) {
      setDefaults(history, sortConfig);
    }
  }, [history, location, sortConfig]);

  const results = useMemo(
    () => deserializeFilterQueryParams(location.search),
    [location]
  );

  return {
    ...results,
    onOffsetChange: (offset: number) => onOffsetChange(history, offset),
    onQueryChange: (q: string) => onQueryChange(history, q),
  };
};

export const useFilterWithSort = <T>(sortConfig: SortConfig<T>) => {
  const history = useHistory();

  const parsedSortParams = deserializeSortQueryParams(history, sortConfig);

  return {
    ...useFilter(sortConfig),
    ...parsedSortParams,
    onSortChange: (sorts: SortDirection<any>[]) => onSortChange(history, sorts),
    onSortingChange: (sorts: SortingState) =>
      onSortChange(history, mapSortingState(sorts)),
    mapSortingState,
  };
};
